import { CreatorCollectionCard } from '@eventbrite/creator-collection-card';
import { GenericLazyString } from '@eventbrite/i18n';
import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import React, { ReactElement } from 'react';
import { BotCollectionContent } from '../../components/BotCollectionContent';
import { SimpleCarousel, SlideConfig } from '../../components/SimpleCarousel';
import { useFeaturedCollectionsForPlace } from '../../hooks';
import { CollectionContentShape } from '../../types';
import {
    GA_CLICK_VIEW_COLLECTION_ACTION,
    GA_EDITORIAL_COLLECTION_CATEGORY,
    GA_VIEW_NEXT_ACTION,
    GA_VIEW_PREVIOUS_ACTION,
} from './constants';

const EVENTBRITE_LOGO =
    'https://cdn.evbuc.com/images/100912392/438776807040/1/original.20200513-210241';

const CREATOR_NAME = {
    EVENTBRITE: 'Eventbrite',
};

const handleCollectionClick = (id: string | undefined) => {
    trackAnalyticsEvent({
        category: GA_EDITORIAL_COLLECTION_CATEGORY,
        action: GA_CLICK_VIEW_COLLECTION_ACTION,
        label: id,
    });
};

const handleNavigationClick = (direction: string) => {
    const action =
        direction === 'left' ? GA_VIEW_PREVIOUS_ACTION : GA_VIEW_NEXT_ACTION;

    trackAnalyticsEvent({
        category: GA_EDITORIAL_COLLECTION_CATEGORY,
        action: action,
    });
};

interface CollectionsBucketProps {
    /**
     * Either placeId or 'online'
     */
    currentPlace: string | string[];
    /**
     * Content for initial hydration if
     * provided.
     */
    content?: CollectionContentShape[];
    /**
     * Current request is from a BOT.
     * If true will render a minimal
     * view with all links rendered.
     */
    isBotRequest?: boolean;
    /**
     * Used to filter returned collections
     * based on the TLD of the request
     */
    tld: string;
    /**
     * Number of collections to display on
     * desktop screens
     */
    cardsPerSlide?: SlideConfig;
    /**
     * Title of the collection carousel
     */
    headerTitle?: string | GenericLazyString | ReactElement;
    /**
     * Subtitle of the collection carousel
     */
    headerSubtitle?: string | GenericLazyString | ReactElement;
    /**
     * When set to true, any off screen carousel
     * elements will not be rendered until on
     * screen.
     */
    lazilyRenderOffScreenElements?: boolean;
    /**
     * List of collection IDs that shouldn't
     * be rendered.
     */
    collectionBlockList?: string[];
    /**
     * Callback triggered if the module will
     * successfully render because there are
     * collections to view for the current
     * place and TLD.
     */
    onMount?: (shouldRender: boolean) => void;
    /**
     * Collection card container classname
     */
    collectionCardContainerClassname?: string;
    /**
     * Boolean to control whether to render collections
     * with zero events.
     *
     * Defaults to false
     */
    renderCollectionsWithZeroEvents?: boolean;
}

export const CollectionCarousel: React.FunctionComponent<
    CollectionsBucketProps
> = (props) => {
    const {
        collectionBlockList,
        collectionCardContainerClassname,
        content,
        currentPlace,
        isBotRequest,
        onMount,
        tld,
        renderCollectionsWithZeroEvents,
    } = props;

    const filterCollectionConfig = {
        tld,
        collectionBlockList,
        renderCollectionsWithZeroEvents,
    };
    const { data: filteredCollections, isLoading } =
        useFeaturedCollectionsForPlace(
            currentPlace,
            filterCollectionConfig,
            content,
        );

    const shouldRender =
        Array.isArray(filteredCollections) && filteredCollections.length > 0;

    React.useEffect(() => {
        if (!isLoading) {
            onMount?.(shouldRender);
        }
    }, [onMount, shouldRender, isLoading]);

    if (!filteredCollections) {
        return null;
    }

    if (shouldRender && isBotRequest) {
        return <BotCollectionContent collectionContent={filteredCollections} />;
    }

    if (shouldRender) {
        return (
            <SimpleCarousel
                slidesVisible={props.cardsPerSlide}
                headerTitle={props.headerTitle}
                subtitle={props.headerSubtitle}
                onNavigationClick={handleNavigationClick}
                lazilyRenderOffScreenElements={
                    props.lazilyRenderOffScreenElements
                }
            >
                {filteredCollections.map(
                    ({
                        id,
                        imageLarge = {},
                        name,
                        summary,
                        eventCount,
                        displayCreatorName,
                        creatorImageIdOverrideUrl,
                    }) => {
                        return (
                            <div
                                style={{ width: '95%' }}
                                key={name}
                                data-testid="collection-carousel-slide"
                            >
                                <CreatorCollectionCard
                                    slug={`/c/${id}`}
                                    name={name}
                                    summary={summary}
                                    id={id}
                                    hideBoxShadow={true}
                                    imageUrl={imageLarge.url}
                                    numUpcomingEvents={eventCount}
                                    onClick={(id: string | undefined) =>
                                        handleCollectionClick(id)
                                    }
                                    shouldOpenNewTab={true}
                                    organizer={{
                                        name:
                                            displayCreatorName ||
                                            CREATOR_NAME.EVENTBRITE,
                                        imageUrl:
                                            creatorImageIdOverrideUrl ||
                                            EVENTBRITE_LOGO,
                                    }}
                                    __containerClassName={
                                        collectionCardContainerClassname
                                    }
                                />
                            </div>
                        );
                    },
                )}
            </SimpleCarousel>
        );
    }

    return null;
};

export default CollectionCarousel;
